import params from '@/lib/calculationparams';
import {
  cappedSalary, correctionFactor, incomeLossRatio, lookupTable,
} from '@/lib/calculations/helpers';
import {
  calculateIVAAmount,
  calculateLAUAmount,
  calculateLGUAmount,
  calculateVVUAmount,
  hasLAU,
  hasVVU,
} from '@/lib/calculations/uwv';
import { CalculationState } from '@/lib/calculationstate';
import { round } from 'lodash-es';

/**
 * Determines whether the current calculation state is eligible for a WBO
 * (WIA Werknemersvezekering bij Beperkte Arbeidsongeschiktheid).
 *
 * @param state
 */
export function hasWBO(state: CalculationState): boolean {
  return state.disabilityRatio < 0.35 && state.disabilityRatio >= 0.15
    && incomeLossRatio(state) >= params.nvschade.wboMinimumSalaryLossRatio
    && state.extraIncome > 0;
}

/**
 * Calculates the amount awarded by a WBO-payout.
 * (WIA Werknemersvezekering bij Beperkte Arbeidsongeschiktheid).
 *
 * Returns 0 if the state is not eligible for a WBO.
 *
 * @param state
 */
export function calculateWBOAmount(state: CalculationState): number {
  if (!hasWBO(state)) return 0;

  const modifier = Math.min(incomeLossRatio(state), state.disabilityRatio);
  const payout = modifier * cappedSalary(state);

  // TODO: migrate to some non floating point number type
  return round(payout, 2);
}

/**
 * Determines whether the current calculation state is eligible for a WHA
 * (WIA-Werknemersuitkering bij Gedeeltelijke Arbeidsongeschiktheid).
 *
 * @param state
 */
export function hasWHA(state: CalculationState): boolean {
  return state.disabilityRatio >= 0.35 && state.disabilityRatio < 0.8
    && (hasLAU(state) || hasVVU(state));
}

/**
 * Calculates the amount awarded by a WHA-payout.
 * (WIA-Werknemersuitkering bij Gedeeltelijke Arbeidsongeschiktheid).
 *
 * Returns 0 if the state is not eligible for a WHA.
 *
 * @param state
 */
export function calculateWHAAmount(state: CalculationState): number {
  if (!hasWHA(state)) return 0;

  const partCappedSalary = params.nvschade.whaCappedSalaryPercent * cappedSalary(state);
  const lau = calculateLAUAmount(state);
  const vvu = calculateVVUAmount(state);
  const partCorrectedIncome = params.nvschade.whaCurrentIncomePercent * state.extraIncome * correctionFactor(state);

  const payout = partCappedSalary - lau - vvu - partCorrectedIncome;

  // TODO: migrate to some non floating point number type
  return round(payout, 2);
}

/**
 * Determines whether the current calculation state is eligible for a WEB
 * (WIA-Excedent bij Beperkte Arbeidsongeschiktheid).
 *
 * @param state
 */
export function hasWEB(state: CalculationState): boolean {
  return state.disabilityRatio < 0.35
    && incomeLossRatio(state) >= params.nvschade.wboMinimumSalaryLossRatio
    && state.extraIncome > 0
    && state.salary > params.uwv.maximumSalary;
}

/**
 * Calculates the amount awarded by a WEB-payoutr
 * (WIA-Excedent bij Beperkte Arbeidsongeschiktheid).
 *
 * Returns 0 if the state is not eligible for a WEB.
 *
 * @param state
 */
export function calculateWEBAmount(state: CalculationState): number {
  if (!hasWEB(state)) return 0;

  const modifier = Math.min(incomeLossRatio(state), state.disabilityRatio);
  const payout = modifier * cappedSalary(state, params.nvschade.excedentMaximum) - calculateWBOAmount(state);

  // TODO: migrate to some non floating point number type
  return round(payout, 2);
}

/**
 * Determines whether the current calculation state is eligible for a WEX when under a LGU.
 * (WIA-Excedent uitkering).
 *
 * @param state
 */
export function hasWEXLGU(state: CalculationState): boolean {
  return state.disabilityRatio >= 0.35
    && (state.disabilityRatio < 0.8 || !state.cannotRecover)
    && state.salary > params.uwv.maximumSalary;
}

/**
 * Calculates the amount awarded by a WEX-payout when under a LGU.
 * (WIA-Excedent Uitkering).
 *
 * Returns 0 if the state is not eligible for a WEX under LGU.
 *
 * @param state
 */
export function calculateWEXLGUAmount(state: CalculationState): number {
  if (!hasWEXLGU(state)) return 0;

  const partCappedSalary = params.nvschade.wexCappedSalaryPercent
    * cappedSalary(state, params.nvschade.excedentMaximum);
  const lgu = calculateLGUAmount(state);
  const partCorrectedIncome = params.nvschade.wexCurrentIncomePercent * state.extraIncome
    * correctionFactor(state, params.nvschade.excedentMaximum);
  const payout = partCappedSalary - lgu - partCorrectedIncome;

  // TODO: migrate to some non floating point number type
  return round(payout, 2);
}

/**
 * Determines whether the current calculation state is eligible for a WEX when under a LAU, VVU, or IVA.
 * (WIA-Excedent uitkering).
 *
 * @param state
 */
export function hasWEX(state: CalculationState): boolean {
  // For LAU/VVU the condition (state.disabilityRatio < 0.8 || !state.cannotRecover) would be true
  // for IVA the condition     (state.disabilityRatio >= 0.8 && state.cannotRecover) would be true
  // these two cancel each other out and will be handled separately in the main function
  return state.disabilityRatio >= 0.35
    && state.salary > params.uwv.maximumSalary;
}

/**
 * Calculates the amount awarded by a WEX-payout when under a LAU, VVU, or IVA.
 * (WIA-Excedent Uitkering).
 *
 * Returns 0 if the state is not eligible for a WEX under LAU/VVU/IVA.
 *
 * @param state
 */
export function calculateWEXAmount(state: CalculationState): number {
  if (!hasWEX(state)) return 0;

  if (state.disabilityRatio >= 0.8 && state.cannotRecover) {
    // IVA specific calculation
    const partCappedSalary = params.nvschade.wexIvaCappedSalaryPercent
      * cappedSalary(state, params.nvschade.excedentMaximum);
    const iva = calculateIVAAmount(state);
    const partCorrectedIncome = params.nvschade.wexIvaCurrentIncomePercent * state.extraIncome
      * correctionFactor(state, params.nvschade.excedentMaximum);

    const payout = partCappedSalary - iva - partCorrectedIncome;
    // TODO: migrate to some non floating point number type
    return round(payout, 2);
  }

  // LAU/VVU specific calculation
  const partCappedSalary = params.nvschade.wexCappedSalaryPercent
    * cappedSalary(state, params.nvschade.excedentMaximum);
  const lau = calculateLAUAmount(state);
  const vvu = calculateVVUAmount(state);
  const wha = calculateWHAAmount(state);
  const partCorrectedIncome = params.nvschade.wexCurrentIncomePercent * state.extraIncome
    * correctionFactor(state, params.nvschade.excedentMaximum);

  const payout = partCappedSalary - lau - vvu - wha - partCorrectedIncome;
  // TODO: migrate to some non floating point number type
  return round(payout, 2);
}

/**
 * Determines whether the current calculation state is eligible for a WET.
 * (WIA-Excedent Top uitkering).
 *
 * @param state
 */
export function hasWET(state: CalculationState): boolean {
  // WET has two variants, but the checks collapse the same as WEX
  return state.disabilityRatio >= 0.35
    && state.salary > params.nvschade.excedentMaximum;
}

/**
 * Calculates the amount awarded by a WET-payout.
 * (WIA-Excedent TOP Uitkering).
 *
 * Returns 0 if the state is not eligible for a WET.
 *
 * @param state
 */
export function calculateWETAmount(state: CalculationState): number {
  if (!hasWET(state)) return 0;

  const salary = (cappedSalary(state, params.nvschade.excedentTopMaximum) - params.nvschade.excedentMaximum);

  let payout;
  let currentPayout;
  if (state.disabilityRatio >= 0.8 && state.cannotRecover) {
    // IVA specific calculation
    payout = params.nvschade.wetIvaCappedSalaryPercent * salary;

    currentPayout = state.extraIncome
      + calculateIVAAmount(state)
      + calculateWHAAmount(state)
      + calculateWEXAmount(state);
  } else {
    // Non IVA specific calculation
    const multiplier = lookupTable(params.nvschade.wetPayoutPercentages, state.disabilityRatio) ?? 0;
    payout = multiplier * params.nvschade.wetCappedSalaryPercent * salary;

    currentPayout = state.extraIncome
      + calculateLAUAmount(state)
      + calculateVVUAmount(state)
      + calculateWHAAmount(state)
      + calculateWEXAmount(state);
  }

  if (currentPayout + payout >= state.salary) {
    payout = state.salary - currentPayout;
  }
  // TODO: migrate to some non floating point number type
  return round(payout, 2);
}

/**
 * Calculate the premium for the given selected insurance(s).
 * This only takes WHA and WEX into account, as WET is not served on a percentage basis.
 *
 * @param state
 */
export function calculatePremium(state: CalculationState): number {
  let premium = 0;

  if (state.insurance.hasWIAEmployee) {
    premium += cappedSalary(state, params.uwv.maximumSalary) * params.nvschade.whaPremium;
  }

  if (state.insurance.hasWIAExcedent) {
    const salaryForPremium = Math.max(
      cappedSalary(state, params.nvschade.excedentMaximum) - params.uwv.maximumSalary,
      0,
    );
    premium += salaryForPremium * params.nvschade.wexPremium;
  }

  return round(premium, 2);
}
